import React, { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Grid, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import FavoriteIcon from '@material-ui/icons/Favorite';
import HomeIcon from '@material-ui/icons/Home';
import GroupIcon from '@material-ui/icons/Group';
import InfoBox from './InfoBox';
import numeral from 'numeral';
import { get, capitalize } from 'lodash';

const WealthSection = ({ user, showUserSide, classes }) => (
  <Grid container spacing={0}>
    <Grid item xs={4}>
      <InfoBox title="Commissions Revenue">
        {capitalize(numeral(get(user, 'transactional_revenue', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Fee Revenue">
        {capitalize(numeral(get(user, 'fee_based_revenue', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Trail Revenue">
        {capitalize(numeral(get(user, 'trailer_revenue', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Direct Revenue">
        {capitalize(numeral(get(user, 'direct_revenue', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      {!showUserSide && (
        <InfoBox title="AUM">{capitalize(numeral(get(user, 'total_assets', 0)).format('$ 0 a'))}</InfoBox>
      )}
      {showUserSide && (
        <InfoBox title="AUM">
          {capitalize(numeral(get(user, 'assets_under_management', 0)).format('$ 0 a'))}
        </InfoBox>
      )}
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Clients">{get(user, 'number_of_clients', 'N/A')}</InfoBox>
    </Grid>
  </Grid>
);

const LifeInsuranceSection = ({ user, classes }) => (
  <Grid container spacing={0}>
    <Grid item xs={4}>
      <InfoBox title="First Year Comms">
        {capitalize(numeral(get(user, 'li_first_year_commissions', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Renewal Comms">
        {capitalize(numeral(get(user, 'li_renewal_commissions', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Override Comms">
        {capitalize(numeral(get(user, 'li_override_commissions', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Other Revenue">
        {capitalize(numeral(get(user, 'li_other_revenues', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Policy Count">
        {get(user, 'li_policy_count', 0) || 'N/A'}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Persistency">
        {get(user, 'li_persistency') ? `${numeral(get(user, 'li_persistency', 0) * 100).format('0')}%` : 'N/A'}
      </InfoBox>
    </Grid>
  </Grid>
);

const PropertyCasualtySection = ({ user, classes }) => (
  <Grid container spacing={0}>
    <Grid item xs={4}>
      <InfoBox title="Personal Premium">
        {capitalize(numeral(get(user, 'pc_personal_lines_premium', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Personal Rate">
        {get(user, 'pc_personal_lines_commission_rate') 
          ? `${numeral(get(user, 'pc_personal_lines_commission_rate', 0) * 100).format('0.0')}%` 
          : 'N/A'}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Personal Retain">
        {get(user, 'pc_personal_retention_rate') 
          ? `${numeral(get(user, 'pc_personal_retention_rate', 0) * 100).format('0.0')}%` 
          : 'N/A'}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Comm'l Premium">
        {capitalize(numeral(get(user, 'pc_commercial_lines_premium', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Comm'l Rate">
        {get(user, 'pc_commercial_lines_commission_rate') 
          ? `${numeral(get(user, 'pc_commercial_lines_commission_rate', 0) * 100).format('0.0')}%` 
          : 'N/A'}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Comm'l Retain">
        {get(user, 'pc_commercial_retention_rate') 
          ? `${numeral(get(user, 'pc_commercial_retention_rate', 0) * 100).format('0.0')}%` 
          : 'N/A'}
      </InfoBox>
    </Grid>
  </Grid>
);

const GroupInsuranceSection = ({ user, classes }) => (
  <Grid container spacing={0}>
    <Grid item xs={4}>
      <InfoBox title="First Year Comms">
        {capitalize(numeral(get(user, 'group_first_year_commissions', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Renewal Comms">
        {capitalize(numeral(get(user, 'group_renewal_commissions', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Override Comms">
        {capitalize(numeral(get(user, 'group_override_commissions', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Other Revenue">
        {capitalize(numeral(get(user, 'group_other_revenues', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="# of Cases">
        {get(user, 'group_number_of_cases', 0) || 'N/A'}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Avg Case Size">
        {capitalize(numeral(get(user, 'group_average_case_size', 0)).format('$ 0 a'))}
      </InfoBox>
    </Grid>
    <Grid item xs={4}>
      <InfoBox title="Retention">
        {get(user, 'group_case_retention_rate') 
          ? `${numeral(get(user, 'group_case_retention_rate', 0) * 100).format('0.0')}%` 
          : 'N/A'}
      </InfoBox>
    </Grid>
  </Grid>
);

const PandLDataMultiLine = ({ classes, user, company, showUserSide }) => {
  const featureTypes = get(company, 'feature_types', []);
  
  const showWealth = featureTypes.includes('multi_line_valuation_wealth_management');
  const showLife = featureTypes.includes('multi_line_valuation_life_insurance');
  const showPandC = featureTypes.includes('multi_line_valuation_p_and_c');
  const showGroup = featureTypes.includes('multi_line_valuation_group');

  // Determine which panels should be expanded by default
  const [wealthExpanded, setWealthExpanded] = useState(showWealth);
  const [lifeExpanded, setLifeExpanded] = useState(showLife && !showWealth);
  const [pandcExpanded, setPandcExpanded] = useState(showPandC && !showWealth && !showLife);
  const [groupExpanded, setGroupExpanded] = useState(showGroup && !showWealth && !showLife && !showPandC);

  return (
    <div className={classes.container}>
      {showWealth && (
        <ExpansionPanel 
          expanded={wealthExpanded} 
          onChange={() => setWealthExpanded(!wealthExpanded)}
          className={classes.panel}
        >
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            classes={{ root: classes.panelHeader }}
          >
            <ListItemIcon className={classes.icon}>
              <AccountBalanceIcon />
            </ListItemIcon>
            <Typography className={classes.heading}>Wealth Management</Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails className={classes.panelDetails}>
            <WealthSection user={user} showUserSide={showUserSide} classes={classes} />
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}

      {showLife && (
        <ExpansionPanel 
          expanded={lifeExpanded} 
          onChange={() => setLifeExpanded(!lifeExpanded)}
          className={classes.panel}
        >
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            classes={{ root: classes.panelHeader }}
          >
            <ListItemIcon className={classes.icon}>
              <FavoriteIcon />
            </ListItemIcon>
            <Typography className={classes.heading}>Life Insurance</Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails className={classes.panelDetails}>
            <LifeInsuranceSection user={user} classes={classes} />
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}

      {showPandC && (
        <ExpansionPanel 
          expanded={pandcExpanded} 
          onChange={() => setPandcExpanded(!pandcExpanded)}
          className={classes.panel}
        >
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            classes={{ root: classes.panelHeader }}
          >
            <ListItemIcon className={classes.icon}>
              <HomeIcon />
            </ListItemIcon>
            <Typography className={classes.heading}>Property & Casualty</Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails className={classes.panelDetails}>
            <PropertyCasualtySection user={user} classes={classes} />
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}

      {showGroup && (
        <ExpansionPanel 
          expanded={groupExpanded} 
          onChange={() => setGroupExpanded(!groupExpanded)}
          className={classes.panel}
        >
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            classes={{ root: classes.panelHeader }}
          >
            <ListItemIcon className={classes.icon}>
              <GroupIcon />
            </ListItemIcon>
            <Typography className={classes.heading}>Group Insurance</Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails className={classes.panelDetails}>
            <GroupInsuranceSection user={user} classes={classes} />
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}
    </div>
  );
};

PandLDataMultiLine.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object,
  company: PropTypes.object,
  showUserSide: PropTypes.bool,
};

PandLDataMultiLine.defaultProps = {
  user: {},
  company: {},
  showUserSide: false,
};

const styles = {
  container: {
    width: '100%',
    padding: '8px 0',
  },
  panel: {
    marginBottom: 8,
    '&:before': {
      display: 'none',
    },
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.12)',
    borderRadius: 4,
  },
  panelHeader: {
    backgroundColor: '#FAFAFA',
    '&:hover': { 
      backgroundColor: '#F0F0F0' 
    },
    minHeight: 48,
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  panelDetails: {
    padding: '8px 16px 16px',
  },
  heading: {
    fontWeight: 500,
    fontSize: '0.9375rem',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  icon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
};

export default withStyles(styles)(PandLDataMultiLine);
